import React, { useState, useEffect, useRef } from 'react';
import {
  DocumentArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import SignatureCanvas from 'react-signature-canvas';

const RegistrationForm = ({ sport, onSubmit, initialData = null }) => {
  const [formData, setFormData] = useState({
    // Student Information
    studentFirstName: '',
    studentLastName: '',
    studentDateOfBirth: '',
    studentGrade: '',
    studentGender: '',
    studentEmail: '',
    studentPhone: '',
    studentAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    
    // Parent/Guardian Information
    parentFirstName: '',
    parentLastName: '',
    parentEmail: '',
    parentPhone: '',
    parentRelationship: 'parent',
    parentAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    sameAsStudentAddress: true,
    
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    
    // Medical Information
    medicalConditions: '',
    medications: '',
    allergies: '',
    physicianName: '',
    physicianPhone: '',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    
    // Sport-Specific Information
    previousExperience: '',
    position: '',
    specialRequests: '',
    
    // Consent and Agreements
    consentToTreat: false,
    liabilityWaiver: false,
    codeOfConduct: false,
    mediaRelease: false,
    transportationConsent: false,
    
    // Documents
    documents: {
      physicalForm: null,
      insuranceCard: null,
      birthCertificate: null,
      additionalDocuments: []
    }
  });
  
  const [signatures, setSignatures] = useState({
    parentSignature: null,
    studentSignature: null
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentSignatureType, setCurrentSignatureType] = useState(null);
  
  const parentSignatureRef = useRef();
  const studentSignatureRef = useRef();
  
  // Initialize form data if editing
  useEffect(() => {
    if (initialData) {
      setFormData({ ...formData, ...initialData });
    }
  }, [initialData]);
  
  // Handle input changes
  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };
  
  // Handle address sync
  const handleAddressSync = (checked) => {
    setFormData(prev => ({
      ...prev,
      sameAsStudentAddress: checked,
      parentAddress: checked ? prev.studentAddress : {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      }
    }));
  };
  
  // Handle file uploads
  const handleFileUpload = async (field, file) => {
    if (!file) return;
    
    // Validate file type and size
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [field]: 'Please upload a valid file (JPG, PNG, PDF, DOC, DOCX)' }));
      return;
    }
    
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, [field]: 'File size must be less than 10MB' }));
      return;
    }
    
    // Simulate upload progress
    setUploadProgress(prev => ({ ...prev, [field]: 0 }));
    
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        const newProgress = prev[field] + 10;
        if (newProgress >= 100) {
          clearInterval(interval);
          // Update form data with file
          if (field === 'additionalDocuments') {
            setFormData(prev => ({
              ...prev,
              documents: {
                ...prev.documents,
                additionalDocuments: [...prev.documents.additionalDocuments, file]
              }
            }));
          } else {
            setFormData(prev => ({
              ...prev,
              documents: {
                ...prev.documents,
                [field]: file
              }
            }));
          }
          return { ...prev, [field]: null };
        }
        return { ...prev, [field]: newProgress };
      });
    }, 100);
  };
  
  // Handle signature
  const handleSignature = (type) => {
    setCurrentSignatureType(type);
    setShowSignatureModal(true);
  };
  
  const saveSignature = () => {
    const canvas = currentSignatureType === 'parent' ? parentSignatureRef.current : studentSignatureRef.current;
    if (canvas && !canvas.isEmpty()) {
      const signatureData = canvas.toDataURL();
      setSignatures(prev => ({
        ...prev,
        [`${currentSignatureType}Signature`]: signatureData
      }));
      setShowSignatureModal(false);
      setCurrentSignatureType(null);
    }
  };
  
  const clearSignature = () => {
    const canvas = currentSignatureType === 'parent' ? parentSignatureRef.current : studentSignatureRef.current;
    if (canvas) {
      canvas.clear();
    }
  };
  
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    const requiredFields = [
      'studentFirstName', 'studentLastName', 'studentDateOfBirth', 'studentGrade',
      'parentFirstName', 'parentLastName', 'parentEmail', 'parentPhone',
      'emergencyContactName', 'emergencyContactPhone', 'emergencyContactRelationship'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field] || formData[field].trim() === '') {
        newErrors[field] = 'This field is required';
      }
    });
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.parentEmail && !emailRegex.test(formData.parentEmail)) {
      newErrors.parentEmail = 'Please enter a valid email address';
    }
    
    // Phone validation
    const phoneRegex = /^[\d\s\-\(\)\+]+$/;
    if (formData.parentPhone && !phoneRegex.test(formData.parentPhone)) {
      newErrors.parentPhone = 'Please enter a valid phone number';
    }
    
    // Required consents
    const requiredConsents = ['consentToTreat', 'liabilityWaiver', 'codeOfConduct'];
    requiredConsents.forEach(consent => {
      if (!formData[consent]) {
        newErrors[consent] = 'This consent is required';
      }
    });
    
    // Required documents
    if (!formData.documents.physicalForm) {
      newErrors.physicalForm = 'Physical form is required';
    }
    
    if (!formData.documents.insuranceCard) {
      newErrors.insuranceCard = 'Insurance card is required';
    }
    
    // Required signatures
    if (!signatures.parentSignature) {
      newErrors.parentSignature = 'Parent/Guardian signature is required';
    }
    
    if (formData.studentGrade >= 9 && !signatures.studentSignature) {
      newErrors.studentSignature = 'Student signature is required for grades 9+';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const registrationData = {
        ...formData,
        sportId: sport.id,
        signatures,
        submittedAt: new Date().toISOString()
      };
      
      await onSubmit(registrationData);
    } catch (error) {
      console.error('Registration submission error:', error);
      setErrors({ submit: 'Failed to submit registration. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {sport.name} Registration
          </h2>
          <p className="text-gray-600 mt-1">
            Please complete all required fields and upload necessary documents.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Student Information */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Student Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.studentFirstName}
                  onChange={(e) => handleInputChange('studentFirstName', e.target.value)}
                  className={`input ${errors.studentFirstName ? 'border-red-500' : ''}`}
                  placeholder="Enter first name"
                />
                {errors.studentFirstName && (
                  <p className="text-red-500 text-sm mt-1">{errors.studentFirstName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.studentLastName}
                  onChange={(e) => handleInputChange('studentLastName', e.target.value)}
                  className={`input ${errors.studentLastName ? 'border-red-500' : ''}`}
                  placeholder="Enter last name"
                />
                {errors.studentLastName && (
                  <p className="text-red-500 text-sm mt-1">{errors.studentLastName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  value={formData.studentDateOfBirth}
                  onChange={(e) => handleInputChange('studentDateOfBirth', e.target.value)}
                  className={`input ${errors.studentDateOfBirth ? 'border-red-500' : ''}`}
                />
                {errors.studentDateOfBirth && (
                  <p className="text-red-500 text-sm mt-1">{errors.studentDateOfBirth}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grade *
                </label>
                <select
                  value={formData.studentGrade}
                  onChange={(e) => handleInputChange('studentGrade', e.target.value)}
                  className={`input ${errors.studentGrade ? 'border-red-500' : ''}`}
                >
                  <option value="">Select grade</option>
                  {[6, 7, 8, 9, 10, 11, 12].map(grade => (
                    <option key={grade} value={grade}>{grade}th Grade</option>
                  ))}
                </select>
                {errors.studentGrade && (
                  <p className="text-red-500 text-sm mt-1">{errors.studentGrade}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  value={formData.studentGender}
                  onChange={(e) => handleInputChange('studentGender', e.target.value)}
                  className="input"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.studentEmail}
                  onChange={(e) => handleInputChange('studentEmail', e.target.value)}
                  className="input"
                  placeholder="student@example.com"
                />
              </div>
            </div>
            
            {/* Student Address */}
            <div className="mt-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Student Address</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={formData.studentAddress.street}
                    onChange={(e) => handleInputChange('studentAddress.street', e.target.value)}
                    className="input"
                    placeholder="123 Main Street"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.studentAddress.city}
                    onChange={(e) => handleInputChange('studentAddress.city', e.target.value)}
                    className="input"
                    placeholder="City"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.studentAddress.state}
                    onChange={(e) => handleInputChange('studentAddress.state', e.target.value)}
                    className="input"
                    placeholder="State"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={formData.studentAddress.zipCode}
                    onChange={(e) => handleInputChange('studentAddress.zipCode', e.target.value)}
                    className="input"
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>
          </section>
          
          {/* Parent/Guardian Information */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Parent/Guardian Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.parentFirstName}
                  onChange={(e) => handleInputChange('parentFirstName', e.target.value)}
                  className={`input ${errors.parentFirstName ? 'border-red-500' : ''}`}
                  placeholder="Enter first name"
                />
                {errors.parentFirstName && (
                  <p className="text-red-500 text-sm mt-1">{errors.parentFirstName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.parentLastName}
                  onChange={(e) => handleInputChange('parentLastName', e.target.value)}
                  className={`input ${errors.parentLastName ? 'border-red-500' : ''}`}
                  placeholder="Enter last name"
                />
                {errors.parentLastName && (
                  <p className="text-red-500 text-sm mt-1">{errors.parentLastName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.parentEmail}
                  onChange={(e) => handleInputChange('parentEmail', e.target.value)}
                  className={`input ${errors.parentEmail ? 'border-red-500' : ''}`}
                  placeholder="parent@example.com"
                />
                {errors.parentEmail && (
                  <p className="text-red-500 text-sm mt-1">{errors.parentEmail}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  value={formData.parentPhone}
                  onChange={(e) => handleInputChange('parentPhone', e.target.value)}
                  className={`input ${errors.parentPhone ? 'border-red-500' : ''}`}
                  placeholder="(555) 123-4567"
                />
                {errors.parentPhone && (
                  <p className="text-red-500 text-sm mt-1">{errors.parentPhone}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationship
                </label>
                <select
                  value={formData.parentRelationship}
                  onChange={(e) => handleInputChange('parentRelationship', e.target.value)}
                  className="input"
                >
                  <option value="parent">Parent</option>
                  <option value="guardian">Guardian</option>
                  <option value="stepparent">Step-parent</option>
                  <option value="grandparent">Grandparent</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            {/* Parent Address */}
            <div className="mt-4">
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id="sameAddress"
                  checked={formData.sameAsStudentAddress}
                  onChange={(e) => handleAddressSync(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="sameAddress" className="text-sm text-gray-700">
                  Same as student address
                </label>
              </div>
              
              {!formData.sameAsStudentAddress && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={formData.parentAddress.street}
                      onChange={(e) => handleInputChange('parentAddress.street', e.target.value)}
                      className="input"
                      placeholder="123 Main Street"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={formData.parentAddress.city}
                      onChange={(e) => handleInputChange('parentAddress.city', e.target.value)}
                      className="input"
                      placeholder="City"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      value={formData.parentAddress.state}
                      onChange={(e) => handleInputChange('parentAddress.state', e.target.value)}
                      className="input"
                      placeholder="State"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      value={formData.parentAddress.zipCode}
                      onChange={(e) => handleInputChange('parentAddress.zipCode', e.target.value)}
                      className="input"
                      placeholder="12345"
                    />
                  </div>
                </div>
              )}
            </div>
          </section>
          
          {/* Emergency Contact */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Emergency Contact
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.emergencyContactName}
                  onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                  className={`input ${errors.emergencyContactName ? 'border-red-500' : ''}`}
                  placeholder="Emergency contact name"
                />
                {errors.emergencyContactName && (
                  <p className="text-red-500 text-sm mt-1">{errors.emergencyContactName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                  className={`input ${errors.emergencyContactPhone ? 'border-red-500' : ''}`}
                  placeholder="(555) 123-4567"
                />
                {errors.emergencyContactPhone && (
                  <p className="text-red-500 text-sm mt-1">{errors.emergencyContactPhone}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationship *
                </label>
                <input
                  type="text"
                  value={formData.emergencyContactRelationship}
                  onChange={(e) => handleInputChange('emergencyContactRelationship', e.target.value)}
                  className={`input ${errors.emergencyContactRelationship ? 'border-red-500' : ''}`}
                  placeholder="Relationship to student"
                />
                {errors.emergencyContactRelationship && (
                  <p className="text-red-500 text-sm mt-1">{errors.emergencyContactRelationship}</p>
                )}
              </div>
            </div>
          </section>
          
          {/* Medical Information */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Medical Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Medical Conditions
                </label>
                <textarea
                  value={formData.medicalConditions}
                  onChange={(e) => handleInputChange('medicalConditions', e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="List any medical conditions, or write 'None'"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Medications
                </label>
                <textarea
                  value={formData.medications}
                  onChange={(e) => handleInputChange('medications', e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="List current medications and dosages, or write 'None'"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allergies
                </label>
                <textarea
                  value={formData.allergies}
                  onChange={(e) => handleInputChange('allergies', e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="List any allergies, or write 'None'"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Physician Name
                  </label>
                  <input
                    type="text"
                    value={formData.physicianName}
                    onChange={(e) => handleInputChange('physicianName', e.target.value)}
                    className="input"
                    placeholder="Dr. Smith"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Physician Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.physicianPhone}
                    onChange={(e) => handleInputChange('physicianPhone', e.target.value)}
                    className="input"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Insurance Provider
                  </label>
                  <input
                    type="text"
                    value={formData.insuranceProvider}
                    onChange={(e) => handleInputChange('insuranceProvider', e.target.value)}
                    className="input"
                    placeholder="Insurance company name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Policy Number
                  </label>
                  <input
                    type="text"
                    value={formData.insurancePolicyNumber}
                    onChange={(e) => handleInputChange('insurancePolicyNumber', e.target.value)}
                    className="input"
                    placeholder="Policy number"
                  />
                </div>
              </div>
            </div>
          </section>
          
          {/* Sport-Specific Information */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {sport.name} Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Previous Experience
                </label>
                <textarea
                  value={formData.previousExperience}
                  onChange={(e) => handleInputChange('previousExperience', e.target.value)}
                  className="input"
                  rows={3}
                  placeholder={`Describe any previous experience with ${sport.name.toLowerCase()}`}
                />
              </div>
              
              {sport.positions && sport.positions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Position
                  </label>
                  <select
                    value={formData.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                    className="input"
                  >
                    <option value="">Select position</option>
                    {sport.positions.map(position => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Special Requests or Notes
                </label>
                <textarea
                  value={formData.specialRequests}
                  onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="Any special requests, accommodations, or additional information"
                />
              </div>
            </div>
          </section>
          
          {/* Document Uploads */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Required Documents
            </h3>
            <div className="space-y-4">
              {/* Physical Form */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Physical Form * <span className="text-gray-500">(PDF, DOC, or Image)</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {formData.documents.physicalForm ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                        <span className="text-sm text-gray-700">
                          {formData.documents.physicalForm.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleInputChange('documents.physicalForm', null)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <DocumentArrowUpIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">
                        <label className="cursor-pointer text-primary hover:text-primary-dark">
                          Click to upload physical form
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileUpload('physicalForm', e.target.files[0])}
                          />
                        </label>
                      </div>
                      {uploadProgress.physicalForm !== undefined && (
                        <div className="mt-2">
                          <div className="bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress.physicalForm}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.physicalForm && (
                  <p className="text-red-500 text-sm mt-1">{errors.physicalForm}</p>
                )}
              </div>
              
              {/* Insurance Card */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Insurance Card * <span className="text-gray-500">(Front and back)</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {formData.documents.insuranceCard ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                        <span className="text-sm text-gray-700">
                          {formData.documents.insuranceCard.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleInputChange('documents.insuranceCard', null)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <DocumentArrowUpIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">
                        <label className="cursor-pointer text-primary hover:text-primary-dark">
                          Click to upload insurance card
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileUpload('insuranceCard', e.target.files[0])}
                          />
                        </label>
                      </div>
                      {uploadProgress.insuranceCard !== undefined && (
                        <div className="mt-2">
                          <div className="bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress.insuranceCard}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.insuranceCard && (
                  <p className="text-red-500 text-sm mt-1">{errors.insuranceCard}</p>
                )}
              </div>
              
              {/* Birth Certificate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Birth Certificate <span className="text-gray-500">(Optional)</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {formData.documents.birthCertificate ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                        <span className="text-sm text-gray-700">
                          {formData.documents.birthCertificate.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleInputChange('documents.birthCertificate', null)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <DocumentArrowUpIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">
                        <label className="cursor-pointer text-primary hover:text-primary-dark">
                          Click to upload birth certificate
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileUpload('birthCertificate', e.target.files[0])}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Additional Documents */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Documents <span className="text-gray-500">(Optional)</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {formData.documents.additionalDocuments.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {formData.documents.additionalDocuments.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <div className="flex items-center">
                            <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                            <span className="text-sm text-gray-700">{doc.name}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newDocs = formData.documents.additionalDocuments.filter((_, i) => i !== index);
                              setFormData(prev => ({
                                ...prev,
                                documents: {
                                  ...prev.documents,
                                  additionalDocuments: newDocs
                                }
                              }));
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="text-center">
                    <DocumentArrowUpIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <div className="text-sm text-gray-600">
                      <label className="cursor-pointer text-primary hover:text-primary-dark">
                        Click to upload additional documents
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileUpload('additionalDocuments', e.target.files[0])}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          {/* Consent and Agreements */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Consent and Agreements
            </h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="consentToTreat"
                  checked={formData.consentToTreat}
                  onChange={(e) => handleInputChange('consentToTreat', e.target.checked)}
                  className={`mt-1 mr-3 ${errors.consentToTreat ? 'border-red-500' : ''}`}
                />
                <div>
                  <label htmlFor="consentToTreat" className="text-sm font-medium text-gray-700">
                    Medical Treatment Consent *
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    I give consent for emergency medical treatment of my child if I cannot be reached immediately.
                  </p>
                  {errors.consentToTreat && (
                    <p className="text-red-500 text-sm mt-1">{errors.consentToTreat}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="liabilityWaiver"
                  checked={formData.liabilityWaiver}
                  onChange={(e) => handleInputChange('liabilityWaiver', e.target.checked)}
                  className={`mt-1 mr-3 ${errors.liabilityWaiver ? 'border-red-500' : ''}`}
                />
                <div>
                  <label htmlFor="liabilityWaiver" className="text-sm font-medium text-gray-700">
                    Liability Waiver *
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    I understand the risks involved in athletic participation and waive liability claims against East End Prep.
                  </p>
                  {errors.liabilityWaiver && (
                    <p className="text-red-500 text-sm mt-1">{errors.liabilityWaiver}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="codeOfConduct"
                  checked={formData.codeOfConduct}
                  onChange={(e) => handleInputChange('codeOfConduct', e.target.checked)}
                  className={`mt-1 mr-3 ${errors.codeOfConduct ? 'border-red-500' : ''}`}
                />
                <div>
                  <label htmlFor="codeOfConduct" className="text-sm font-medium text-gray-700">
                    Code of Conduct Agreement *
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    My child and I agree to abide by the East End Prep Athletic Code of Conduct.
                  </p>
                  {errors.codeOfConduct && (
                    <p className="text-red-500 text-sm mt-1">{errors.codeOfConduct}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="mediaRelease"
                  checked={formData.mediaRelease}
                  onChange={(e) => handleInputChange('mediaRelease', e.target.checked)}
                  className="mt-1 mr-3"
                />
                <div>
                  <label htmlFor="mediaRelease" className="text-sm font-medium text-gray-700">
                    Media Release
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    I give permission for my child's image to be used in school publications and media.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="transportationConsent"
                  checked={formData.transportationConsent}
                  onChange={(e) => handleInputChange('transportationConsent', e.target.checked)}
                  className="mt-1 mr-3"
                />
                <div>
                  <label htmlFor="transportationConsent" className="text-sm font-medium text-gray-700">
                    Transportation Consent
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    I give permission for my child to travel with the team to away games and events.
                  </p>
                </div>
              </div>
            </div>
          </section>
          
          {/* Digital Signatures */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Digital Signatures
            </h3>
            <div className="space-y-4">
              {/* Parent Signature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parent/Guardian Signature *
                </label>
                <div className="border border-gray-300 rounded-lg p-4">
                  {signatures.parentSignature ? (
                    <div className="text-center">
                      <img 
                        src={signatures.parentSignature} 
                        alt="Parent signature" 
                        className="max-h-20 mx-auto border border-gray-200 rounded"
                      />
                      <div className="mt-2 space-x-2">
                        <button
                          type="button"
                          onClick={() => handleSignature('parent')}
                          className="text-sm text-primary hover:text-primary-dark"
                        >
                          <PencilIcon className="w-4 h-4 inline mr-1" />
                          Edit Signature
                        </button>
                        <button
                          type="button"
                          onClick={() => setSignatures(prev => ({ ...prev, parentSignature: null }))}
                          className="text-sm text-red-500 hover:text-red-700"
                        >
                          <TrashIcon className="w-4 h-4 inline mr-1" />
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <PencilIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <button
                        type="button"
                        onClick={() => handleSignature('parent')}
                        className="btn-secondary"
                      >
                        Add Parent Signature
                      </button>
                    </div>
                  )}
                </div>
                {errors.parentSignature && (
                  <p className="text-red-500 text-sm mt-1">{errors.parentSignature}</p>
                )}
              </div>
              
              {/* Student Signature (for grades 9+) */}
              {formData.studentGrade >= 9 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Signature * <span className="text-gray-500">(Required for grades 9+)</span>
                  </label>
                  <div className="border border-gray-300 rounded-lg p-4">
                    {signatures.studentSignature ? (
                      <div className="text-center">
                        <img 
                          src={signatures.studentSignature} 
                          alt="Student signature" 
                          className="max-h-20 mx-auto border border-gray-200 rounded"
                        />
                        <div className="mt-2 space-x-2">
                          <button
                            type="button"
                            onClick={() => handleSignature('student')}
                            className="text-sm text-primary hover:text-primary-dark"
                          >
                            <PencilIcon className="w-4 h-4 inline mr-1" />
                            Edit Signature
                          </button>
                          <button
                            type="button"
                            onClick={() => setSignatures(prev => ({ ...prev, studentSignature: null }))}
                            className="text-sm text-red-500 hover:text-red-700"
                          >
                            <TrashIcon className="w-4 h-4 inline mr-1" />
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <PencilIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <button
                          type="button"
                          onClick={() => handleSignature('student')}
                          className="btn-secondary"
                        >
                          Add Student Signature
                        </button>
                      </div>
                    )}
                  </div>
                  {errors.studentSignature && (
                    <p className="text-red-500 text-sm mt-1">{errors.studentSignature}</p>
                  )}
                </div>
              )}
            </div>
          </section>
          
          {/* Submit Button */}
          <div className="border-t border-gray-200 pt-6">
            {errors.submit && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
                  <p className="text-red-700">{errors.submit}</p>
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => window.history.back()}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner w-4 h-4 mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Submit Registration'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {currentSignatureType === 'parent' ? 'Parent/Guardian' : 'Student'} Signature
              </h3>
              <button
                onClick={() => setShowSignatureModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="border border-gray-300 rounded-lg mb-4">
              <SignatureCanvas
                ref={currentSignatureType === 'parent' ? parentSignatureRef : studentSignatureRef}
                canvasProps={{
                  width: 400,
                  height: 200,
                  className: 'signature-canvas'
                }}
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={clearSignature}
                className="btn-secondary"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setShowSignatureModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveSignature}
                className="btn-primary"
              >
                Save Signature
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationForm;